/**
 * Bulk operations UI for the admin "All Listings" view:
 *   • BulkActionBar — floating sticky bar shown when 1+ listings are
 *     selected. Lets the admin deactivate / reactivate the selection
 *     and open the BulkEditDialog.
 *   • BulkEditDialog — small modal that lets you push a shared field
 *     (description, amenities, address) to every selected listing in
 *     parallel.
 *
 * Both use the existing per-listing admin PUT endpoint, fanned out
 * client-side. That's intentional — no new backend endpoint to maintain
 * and each call goes through the same JWT-or-password auth gate already
 * in place.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader2, X, EyeOff, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const HEADERS_JSON = { 'Content-Type': 'application/json' };

function jwtHeaders() {
  if (typeof window === 'undefined') return {};
  const token = localStorage.getItem('app_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function putOne(unitId, payload) {
  const res = await fetch(`${API}/api/public/admin/listings/${unitId}`, {
    method: 'PUT',
    headers: { ...HEADERS_JSON, ...jwtHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed for ${unitId}`);
  return res.json();
}

export function BulkActionBar({ selectedIds, onClear, adminPass, busy, setBusy, onBulkEdit, onChanged, listings }) {
  const n = selectedIds.size;
  if (n === 0) return null;

  const runBulk = async (mutator) => {
    setBusy(true);
    let ok = 0; let fail = 0;
    const ids = [...selectedIds];
    for (const id of ids) {
      try { await mutator(id); ok += 1; }
      catch { fail += 1; }
    }
    setBusy(false);
    if (fail === 0) toast.success(`Updated ${ok} listing${ok === 1 ? '' : 's'}`);
    else toast.error(`${ok} updated, ${fail} failed`);
    onChanged?.();
  };

  // Heuristic: if all selected are currently unlisted, default-action is
  // Reactivate. If any are still listed, default-action is Deactivate.
  const selectedListings = (listings || []).filter(l => selectedIds.has(l.id));
  const anyListed = selectedListings.some(l => l.is_listed !== false);

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-card border border-border/70 rounded-full shadow-xl px-4 py-2 flex items-center gap-3"
      data-testid="bulk-action-bar"
    >
      <button onClick={onClear} className="text-muted-foreground hover:text-foreground" aria-label="Clear selection" data-testid="bulk-clear">
        <X className="h-4 w-4" />
      </button>
      <span className="text-sm font-medium tabular-nums">{n} selected</span>
      <div className="w-px h-5 bg-border" />
      {anyListed ? (
        <Button
          size="sm" variant="outline"
          className="border-amber-500 text-amber-700 hover:bg-amber-50"
          disabled={busy}
          onClick={() => runBulk(id => putOne(id, { password: adminPass, is_listed: false }))}
          data-testid="bulk-deactivate"
        >
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <EyeOff className="h-3 w-3 mr-1" />}
          Unlist
        </Button>
      ) : (
        <Button
          size="sm" variant="outline"
          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          disabled={busy}
          onClick={() => runBulk(id => putOne(id, { password: adminPass, is_listed: true }))}
          data-testid="bulk-reactivate"
        >
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
          List
        </Button>
      )}
      <Button size="sm" variant="outline" disabled={busy} onClick={onBulkEdit} data-testid="bulk-edit">
        <Pencil className="h-3 w-3 mr-1" /> Edit fields…
      </Button>
    </div>
  );
}

const AMENITY_PRESET = [
  'Wi-Fi', 'Kitchen', 'Washer', 'Dryer', 'Air conditioning', 'Heating',
  'TV', 'Pool', 'Free parking', 'Gym', 'Pets allowed', 'Workspace',
];

export function BulkEditDialog({ open, onOpenChange, selectedIds, listings, adminPass, onSaved }) {
  const [touchDesc, setTouchDesc] = useState(false);
  const [touchAmenities, setTouchAmenities] = useState(false);
  const [touchAddress, setTouchAddress] = useState(false);

  const [description, setDescription] = useState('');
  const [amenitiesMode, setAmenitiesMode] = useState('replace'); // 'replace' | 'add'
  const [amenities, setAmenities] = useState([]);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);

  // Auto-suggest defaults from the first selected listing so the user
  // can see what's there and tweak rather than start from blank.
  const firstSelected = useMemo(() => {
    return (listings || []).find(l => selectedIds.has(l.id));
  }, [listings, selectedIds]);

  const toggleAmenity = (a) => setAmenities(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

  const apply = async () => {
    if (!touchDesc && !touchAmenities && !touchAddress) {
      toast.error('Toggle at least one field to bulk-edit.');
      return;
    }
    setBusy(true);
    const ids = [...selectedIds];
    let ok = 0; let fail = 0;
    for (const id of ids) {
      const payload = { password: adminPass };
      if (touchDesc) payload.description = description;
      if (touchAddress) payload.address = address;
      if (touchAmenities) {
        if (amenitiesMode === 'replace') {
          payload.amenities = amenities;
        } else {
          // Additive: union with existing amenities on each listing.
          const existing = ((listings || []).find(l => l.id === id)?.amenities) || [];
          payload.amenities = Array.from(new Set([...existing, ...amenities]));
        }
      }
      try { await putOne(id, payload); ok += 1; }
      catch { fail += 1; }
    }
    setBusy(false);
    if (fail === 0) toast.success(`Updated ${ok} listing${ok === 1 ? '' : 's'}`);
    else toast.error(`${ok} updated, ${fail} failed`);
    if (ok > 0) onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk edit {selectedIds.size} listing{selectedIds.size === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            Only the fields you check below will be pushed to the selected listings — everything else stays untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Description */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={touchDesc} onChange={(e) => setTouchDesc(e.target.checked)} data-testid="bulk-touch-desc" />
              <span className="text-sm font-medium">Description</span>
              {firstSelected?.description && (
                <span className="text-[10px] text-muted-foreground italic">first selected: "{firstSelected.description.slice(0, 40)}…"</span>
              )}
            </label>
            {touchDesc && (
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="New description for all selected listings"
                data-testid="bulk-desc-input"
              />
            )}
          </div>

          {/* Amenities */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={touchAmenities} onChange={(e) => setTouchAmenities(e.target.checked)} data-testid="bulk-touch-amenities" />
              <span className="text-sm font-medium">Amenities</span>
            </label>
            {touchAmenities && (
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-xs">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={amenitiesMode === 'replace'} onChange={() => setAmenitiesMode('replace')} />
                    Replace all
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={amenitiesMode === 'add'} onChange={() => setAmenitiesMode('add')} />
                    Add to existing
                  </label>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {AMENITY_PRESET.map(a => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggleAmenity(a)}
                      className={`px-2 py-1 text-xs rounded-full border ${amenities.includes(a) ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-card border-border hover:bg-muted'}`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={touchAddress} onChange={(e) => setTouchAddress(e.target.checked)} data-testid="bulk-touch-address" />
              <span className="text-sm font-medium">Address</span>
              <span className="text-[10px] text-muted-foreground italic">useful when many units share a building address</span>
            </label>
            {touchAddress && (
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Miami FL"
                data-testid="bulk-address-input"
              />
            )}
            {touchAddress && (
              <p className="text-[11px] text-amber-700">
                Heads up: changing the address won't auto-geocode here. Each listing will re-geocode the first time it's opened.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={apply} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="bulk-edit-apply">
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            Apply to {selectedIds.size}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
